import { useEffect, useState } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/services/api';
import type { AccountType } from '@/types';

type Mode = 'login' | 'signup';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AuthModal() {
  const authModalOpen = useUIStore((s) => s.authModalOpen);
  const authModalMode = useUIStore((s) => s.authModalMode);
  const closeAuthModal = useUIStore((s) => s.closeAuthModal);
  const addNotification = useUIStore((s) => s.addNotification);

  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);

  const [mode, setMode] = useState<Mode>(authModalMode);
  const [accountType, setAccountType] = useState<AccountType>('personal');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Sync the active tab with the mode requested when the modal was opened.
  useEffect(() => {
    if (authModalOpen) {
      setMode(authModalMode);
      setFormError(null);
      setFieldErrors({});
    }
  }, [authModalOpen, authModalMode]);

  // Close on Escape.
  useEffect(() => {
    if (!authModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAuthModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [authModalOpen, closeAuthModal]);

  if (!authModalOpen) return null;

  const switchMode = (next: Mode) => {
    setMode(next);
    setFormError(null);
    setFieldErrors({});
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!EMAIL_RE.test(email.trim())) errors.email = 'Enter a valid email address.';
    if (!password) errors.password = 'Password is required.';
    else if (mode === 'signup' && password.length < 8)
      errors.password = 'Use at least 8 characters.';
    if (mode === 'signup' && accountType === 'business' && !company.trim())
      errors.company = 'Company name is required for business accounts.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!validate()) return;
    setSubmitting(true);
    // Normalize the email before sending (defense-in-depth; the server also normalizes).
    const normalizedEmail = email.trim().toLowerCase();
    try {
      if (mode === 'login') {
        await login(normalizedEmail, password);
      } else {
        await register({
          email: normalizedEmail,
          password,
          name: name.trim() || undefined,
          account_type: accountType,
          company: accountType === 'business' ? company.trim() : undefined,
        });
      }
      addNotification({
        type: 'success',
        message: mode === 'login' ? 'Signed in.' : 'Account created. Welcome to TensorMaps!',
      });
      closeAuthModal();
      resetForm();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setName('');
    setCompany('');
    setEmail('');
    setPassword('');
    setAccountType('personal');
  };

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-fade-in"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeAuthModal();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'login' ? 'Sign in' : 'Create account'}
    >
      <div className="max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto overscroll-contain rounded-2xl bg-white shadow-modal animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <span aria-hidden className="text-lg leading-none">🗺️</span>
            <span className="text-sm font-semibold tracking-tight text-gray-900">
              Tensor<span className="text-primary-600">Maps</span>
            </span>
          </div>
          <button
            onClick={closeAuthModal}
            className="rounded-full p-1 hover:bg-gray-100"
            aria-label="Close"
          >
            <CloseIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 m-4 rounded-lg p-1">
          <TabButton active={mode === 'login'} onClick={() => switchMode('login')}>
            Sign in
          </TabButton>
          <TabButton active={mode === 'signup'} onClick={() => switchMode('signup')}>
            Create account
          </TabButton>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
          {mode === 'signup' && (
            <>
              {/* Personal vs business */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500">
                  Account type
                </label>
                <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                  <SegmentButton
                    active={accountType === 'personal'}
                    onClick={() => setAccountType('personal')}
                  >
                    Personal
                  </SegmentButton>
                  <SegmentButton
                    active={accountType === 'business'}
                    onClick={() => setAccountType('business')}
                  >
                    Business
                  </SegmentButton>
                </div>
              </div>

              <Field
                label="Name"
                value={name}
                onChange={setName}
                placeholder="Your name"
                autoComplete="name"
              />

              {accountType === 'business' && (
                <Field
                  label="Company"
                  value={company}
                  onChange={setCompany}
                  placeholder="Company name"
                  autoComplete="organization"
                  error={fieldErrors.company}
                />
              )}
            </>
          )}

          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            autoComplete="email"
            error={fieldErrors.email}
          />

          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder={mode === 'signup' ? 'At least 8 characters' : 'Your password'}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            error={fieldErrors.password}
          />

          {mode === 'login' && (
            <button
              type="button"
              onClick={async () => {
                const e = email.trim().toLowerCase();
                if (!EMAIL_RE.test(e)) {
                  setFormError('Enter your email above, then tap “Forgot password?”.');
                  return;
                }
                // No account enumeration: the endpoint always 200s, so ignore errors.
                try {
                  await api.post('/auth/forgot-password', { email: e });
                } catch {
                  /* ignore */
                }
                addNotification({
                  type: 'info',
                  message: 'If that email is registered, a password-reset link is on its way.',
                });
              }}
              className="-mt-1 block text-xs font-medium text-primary-600 hover:underline"
            >
              Forgot password?
            </button>
          )}

          {formError && (
            <div className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">
              {formError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting
              ? mode === 'login'
                ? 'Signing in…'
                : 'Creating account…'
              : mode === 'login'
                ? 'Sign in'
                : 'Create account'}
          </button>

          <p className="text-center text-xs text-gray-500">
            {mode === 'login' ? (
              <>
                New to TensorMaps?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="font-medium text-primary-600 hover:underline"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="font-medium text-primary-600 hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-gray-500">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={`block w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors focus:border-primary-500 focus:ring-1 focus:ring-primary-500 ${
          error ? 'border-danger-400' : 'border-gray-300'
        }`}
      />
      {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-md py-1.5 text-sm font-medium transition-colors ${
        active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

function SegmentButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-md py-1.5 text-sm font-medium transition-colors ${
        active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
